/**
 * src/utils/imageCompress.ts — client-side image compression
 *
 * v0.4.9.2.3c22b: stolen vehicles image upload.
 *
 * Compresses a user-picked image (from mobile camera roll or desktop file
 * picker) to a "mobile photo" quality — long side ≤ 480 px, JPEG q=0.8,
 * downscale-only. Output is a base64 data URL the API accepts directly.
 *
 * Why client-side (and not server-side compression):
 *   - The user is on a phone; uploading a full-res 4 MB photo over a slow
 *     connection is wasteful. Compressing before upload saves 80-95 %
 *     bandwidth.
 *   - Server CPU stays free for face/OCR/PKI work, not image re-encoding.
 *   - Matches the pattern the user is already familiar with from
 *     mostWanted face images (server just stores the bytes it receives).
 *
 * Format:
 *   - Output is always `image/jpeg` regardless of the input format
 *     (PNG, HEIC, WebP — Canvas normalizes them). The data URL prefix
 *     becomes `data:image/jpeg;base64,...` which the backend matches.
 *
 * No deps — pure Canvas API + URL.createObjectURL. Works in any modern
 * browser, including the PWA-WebView APK.
 */
export interface CompressOptions {
  /** Longest side in pixels. Default 480. Output is smaller than this if input was smaller. */
  maxDim?: number;
  /** JPEG quality 0..1. Default 0.8. */
  quality?: number;
  /** Output MIME type. Default 'image/jpeg'. */
  mimeType?: string;
}

const DEFAULTS: Required<CompressOptions> = {
  maxDim: 480,
  quality: 0.8,
  mimeType: 'image/jpeg',
};

/**
 * Compress a File picked from a <input type="file"> to a JPEG data URL.
 *
 * @param file - the File from a file input or drag-drop
 * @param opts - override the defaults; pass nothing for "mobile quality"
 * @returns - `data:image/jpeg;base64,...` data URL, base64-encoded
 * @throws - if the image can't be decoded or the canvas produces no output
 */
export async function compressImage(
  file: File,
  opts: CompressOptions = {}
): Promise<string> {
  const { maxDim, quality, mimeType } = { ...DEFAULTS, ...opts };

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const longest = Math.max(img.width, img.height);
    // Downscale-only — never upscale. 1.0 if input is already ≤ maxDim.
    const scale = Math.min(1, maxDim / longest);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');

    // JPEG has no alpha. Paint white first so transparent pixels
    // (PNG / HEIC-with-alpha input) don't compress to muddy gray.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const dataUrl = canvas.toDataURL(mimeType, quality);
    if (!dataUrl || !dataUrl.startsWith('data:')) {
      throw new Error('Canvas produced no output');
    }
    return dataUrl;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = src;
  });
}

/**
 * Convenience: take a File, return a data URL ready for `await api.x({ image: ... })`.
 * Wraps `compressImage` so the caller doesn't need to know about max-dim / quality.
 */
export async function fileToCompressedDataUrl(file: File): Promise<string> {
  return compressImage(file);
}
